import * as React from 'react'
import {
  Link as RouterLink,
  NavLink as RouterNavLink,
  Navigate as RouterNavigate,
  useLocation,
  useNavigate as useRouterNavigate,
} from 'react-router'
import type {
  LinkProps,
  NavLinkProps,
  NavigateFunction,
  NavigateOptions,
  NavigateProps,
  To,
} from 'react-router'

import { getStudioLanguageFromPathname } from './canonicalize-studio-locale-pathname'
import { canonicalizeStudioLocaleLinkTo } from './canonicalize-studio-locale-to'
import {
  getLocaleHistorySegmentSnapshot,
  historyReappliesLocalePrefix,
  subscribeLocaleHistorySegment,
} from './locale-history'

/**
 * The locale the reader is currently browsing under, or `undefined` on an
 * unprefixed (default-language) URL.
 *
 * Under `withLocaleRoutes` this is the first path segment, read from
 * `useLocation()`. Under `createLocaleHistory` the router never sees the
 * prefix — `setLocaleSegment` only replaceState's the address bar — so this
 * subscribes to the history adapter instead. Without that, every `<Link>`
 * keeps the href from the language it first rendered under.
 */
export function useUrlLocale(): string | undefined {
  const pathname = useLocation().pathname
  const historySegment = React.useSyncExternalStore(
    subscribeLocaleHistorySegment,
    getLocaleHistorySegmentSnapshot,
    () => undefined,
  )

  if (historyReappliesLocalePrefix()) {
    return historySegment
  }

  return getStudioLanguageFromPathname(pathname)
}

function localizeTo(to: To, locale: string | undefined): To {
  if (historyReappliesLocalePrefix() || !locale) {
    return to
  }

  return canonicalizeStudioLocaleLinkTo(to, locale)
}

/**
 * `react-router`'s `Link` with the browsing locale reapplied to `to` and `mask`.
 *
 * `ROUTES` values carry no locale, so a bare `<Link to={ROUTES.X}>` drops the
 * prefix and makes the router restore it after the fact — the two-step redirect
 * reported in STU-9610. Prefixing the href instead makes the navigation direct.
 * A localized mask also keeps the visible href correct for copied links and new tabs.
 *
 * When the history adapter already prefixes, `to` stays locale-free so
 * `createHref` can run the slug table. Baking `/de/...` into `to` would make
 * `createHref` treat the prefix as caller-owned and skip translation.
 */
export function Link({ mask, to, ...props }: LinkProps) {
  const locale = useUrlLocale()

  return (
    <RouterLink
      mask={mask ? localizeTo(mask, locale) : mask}
      to={localizeTo(to, locale)}
      {...props}
    />
  )
}

export function NavLink({ to, ...props }: NavLinkProps) {
  const locale = useUrlLocale()

  return <RouterNavLink to={localizeTo(to, locale)} {...props} />
}

/**
 * `react-router`'s `Navigate` with the browsing locale reapplied to `to`.
 *
 * Redirect routes — the `lp/[slug]` fallbacks, the missing-session guards — are
 * the same bug as `Link` with a worse tell: the reader watches the URL drop its
 * prefix and then get it back. Prefixing the target lands them in one hop.
 */
export function Navigate({ to, ...props }: NavigateProps) {
  const locale = useUrlLocale()

  return <RouterNavigate to={localizeTo(to, locale)} {...props} />
}

/**
 * The locale to reapply to a navigation target, read without subscribing.
 *
 * `window.location` is the same source `createBrowserRouter` navigates against,
 * and react-router only commits history once a navigation resolves, so this
 * agrees with `useLocation()` at every point a caller can observe both. Under a
 * `MemoryRouter` there is no address bar to read, so callers get no prefix.
 *
 * Nothing is reapplied when the history already does it. The address bar keeps
 * its prefix under `createLocaleHistory`, so reading one here and prefixing the
 * target would navigate to `/de/de/video`.
 */
function readUrlLocale(): string | undefined {
  if (typeof window === 'undefined' || historyReappliesLocalePrefix()) {
    return undefined
  }

  return getStudioLanguageFromPathname(window.location.pathname)
}

/**
 * `react-router`'s `useNavigate` with the browsing locale reapplied to the target.
 *
 * The locale is read inside the callback instead of during render because, on a
 * data router, `useNavigate` resolves to `useNavigateStable`, which subscribes to
 * nothing. Calling `useLocation()` here would re-render every one of the ~157
 * navigate-only components on each location change, including same-route search
 * param updates. Reading at call time is also the truer reading: it sees the URL
 * the reader is actually on when they act, not the one from the last render.
 */
export function useNavigate(): NavigateFunction {
  const navigate = useRouterNavigate()

  return React.useCallback<NavigateFunction>(
    (to: To | number, options?: NavigateOptions) => {
      if (typeof to === 'number') {
        return navigate(to)
      }

      const locale = readUrlLocale()

      return navigate(locale ? canonicalizeStudioLocaleLinkTo(to, locale) : to, options)
    },
    [navigate],
  )
}
